import dispatcher from "../dispatcher"
import AccountStore from "../stores/AccountStore"
import AuthenticationStore from "../stores/AuthenticationStore"
import HierarchySettingsStore from "../stores/HierarchySettingsStore"
import * as api from "../api"
import * as accountActions from "../actions/AccountActions"
import * as hierarchySettingsActions from "../actions/HierarchySettingsActions"
import * as hierarchyHelper from "../helpers/hierarchyHelper"
import { enableIntercom, getCurrentEnviornment } from "../../intercom"
import strings from "../localization/strings"
import { ELanguage, EPlanType, ESizeOfBusiness, ETypeOfBusiness, IAccountDocument, IAccountStatsDocument, IClientDocument, IUserDocument } from "../models"
import * as userHelper from "../helpers/userHelper"
import * as workflowActions from "../actions/WorkflowActions"

export function ssoLogin() {
    dispatcher.dispatch({ type: "LOGGING_IN" })
}

function loginResult(result) {
    result.data ?
        setAuthData(result) :
        dispatcher.dispatch({ type: "LOGIN_FAILED" })
    return result
}

export function login(email, password) {
    return api.login(email, password).then(res =>
        loginResult(res)
    )
}

export function getSSOAuthData(token) {
    return api.getSSOAuthData(token).then(function (result) {
        loginResult(result)
    })
}
export async function getAuthData(userId) {
    let response
    response = userId ?
        await api.getAuthData(userId) :
        await api.getAuthDataByCookie()
    response.data ?
        setAuthData(response) :
        logout()
}

async function setAuthData(result) {
    const userRole = result.data.Account.AccountSettings.UserRoles.find((item) => item.Id === result.data.User.UserRoleId)
    const isAccountAdmin = userRole ? userRole.Permissions.includes(AuthenticationStore.Permissions.Account.AccountAdmin) : false
    saveUserCookie(result.data.User.Id, result.data.Account.Id, isAccountAdmin)
    recieveAuthData(result.data)
    getAsyncAuthData(result.data.User.Id)
    getAccountsConfig()
    const loggedInUserWorkflows = await getLoggedInUserWorkflows(result.data.User.Id)
    setIntercom(result.data, loggedInUserWorkflows)
}

export async function getLoggedInUserWorkflows(loggedInUserId) {
    const workflows = await workflowActions.fetchUserWorkflows(loggedInUserId)

    //Every 2 days, remind the user if they have pending workflows to finish by loading and displaying the banner. This is only if the app is left open. On login, this will also trigger. 
    let fortyEightHoursInMilliseconds = 172800000
    setInterval(() => workflowActions.fetchUserWorkflows(loggedInUserId), fortyEightHoursInMilliseconds)
    return workflows
}

export function getAccountsConfig() {
    api.getAccountsConfig().then(res =>
        dispatcher.dispatch({ type: "ACCOUNTS_CONFIG", config: res.data })
    )
}

export function getAsyncAuthData(userId) {
    api.getAsyncAuthString(userId).then(function (result) {
        dispatcher.dispatch({ type: "UPDATE_ASYNC_AUTH", data: result.data })
    })
}

export function saveUserCookie(userId, accountId, isAccountAdmin) {
    if (userId && userId.length > 0) {
        localStorage.setItem("findd_userId", userId)
        localStorage.setItem("findd_accountId", accountId)
        if (isAccountAdmin) localStorage.setItem("findd_type", userId + accountId)
        else localStorage.setItem("findd_type", userId)
    }
}

async function setIntercom(data: { Account: IAccountDocument, User: IUserDocument }, loggedInUserWorkflows) {
    if (enableIntercom()) {
        try {
            window["Intercom"]("shutdown")
        } catch (error) {
            console.error(error)
        }

        const accountSetupWorkflow = loggedInUserWorkflows && loggedInUserWorkflows.find(item => item.Workflow.SystemId === "ACCOUNT_SETUP")
        const accountSetupPercentComplete = !accountSetupWorkflow ?
            null :
            Math.round((accountSetupWorkflow.Workflow.CompletedStepCount / accountSetupWorkflow.Workflow.TotalStepCount) * 100)

        if (!data.User.UserRole || !data.User.UserRole.Permissions || !data.User.UserRole.Permissions.includes("GLOBAL_ADMIN")) {
            const clientDoc = (await api.getClientByExternalId(data.Account.Id)).data as IClientDocument
            const accountStatsDoc = (await api.getAccountLatestStats(data.Account.Id)).data as IAccountStatsDocument
            const intercomSettings = {
                app_id: getAppIdForIntercom(),
                name: `${data.User.FirstName} ${data.User.LastName}`,
                email: getCorrectUserEmailForIntercom(data.User.Email, data.Account.IsFinddTestAccount),
                user_id: `${data.User.Id}`,
                created_at: `${data.User.ObjectAudit.CreatedDate}`,
                "Release Notification Email": getReleaseNotificationPreferences(data.User, "SendEmailNotification"),
                "Grandd Central Release Notification Banners": getReleaseNotificationPreferences(data.User, "SendAppNotification"),
                company: {
                    id: data.Account.Id,
                    name: getCorrectCompanyNameForIntercom(data.Account.Name),
                    "Findd Active Account": data.Account.Active,
                    "Findd Test Account": data.Account.IsFinddTestAccount,
                    "Findd Demo Account": (data.Account.AccountSettings && data.Account.AccountSettings.FeatureSettings && data.Account.AccountSettings.FeatureSettings.LoadTestData !== null) ? data.Account.AccountSettings.FeatureSettings.LoadTestData : null,
                    "Company created at": data.Account.ObjectAudit.CreatedDate,
                    "Client-Reported Employee Size": getInfoFromClient(clientDoc, "CompanySize"),
                    "Company industry": getInfoFromClient(clientDoc, "CompanyType"),
                    "Plan Name": data.Account.BillingInfo ? EPlanType[data.Account.BillingInfo.PlanType] : null,
                    "Devices Registered Count": getCountFromStats(accountStatsDoc, "RegisteredDeviceCount"),
                    "Active Jobs Count": getCountFromStats(accountStatsDoc, "ActiveJobCount"),
                    "Active Clients Count": getCountFromStats(accountStatsDoc, "ClientCount"),
                    "Account Users Count": getCountFromStats(accountStatsDoc, "AccountUserCount"),
                    "Account User Roles Count": getCountFromStats(accountStatsDoc, "AccountUserRoleCount"),
                    "Active Employees Count": getCountFromStats(accountStatsDoc, "EmployeesCount"),
                    "Hierarchy Level One Count": getCountFromStats(accountStatsDoc, "HierarchyCountOfChildrenOfAccountLevel"),
                    "Total Punch Count": getCountFromStats(accountStatsDoc, "TotalPunchCount"),
                    "Individual Mode Devices Count": getCountFromStats(accountStatsDoc, "IndividualModeDeviceCount"),
                    "Clock Mode Devices Count": getCountFromStats(accountStatsDoc, "ClockModeDeviceCount"),
                    "Mobile Clock Mode Devices Count": getCountFromStats(accountStatsDoc, "MobileClockModeDeviceCount"),
                    "Account Positions Count": getCountFromStats(accountStatsDoc, "AccountPositionCount"),
                    "Pay Groups Count": getCountFromStats(accountStatsDoc, "PayGroupCount"),
                    "Pay Types Count": getCountFromStats(accountStatsDoc, "PayTypeCount"),
                    "Account Setup Percent Complete": accountSetupPercentComplete
                }
            }
            // Booting Up Intercom
            window["Intercom"]("boot", intercomSettings)
        }
    }
}

export function recieveAuthData(data: { Account: IAccountDocument, User: IUserDocument }) {
    const language = data.User.Language
    strings.setLanguage(language === ELanguage.English ? "en" : language === ELanguage.Español ? "es" : "en")
    dispatcher.dispatch({ type: "RECEIVE_AUTH_DATA", authData: data })
    if (!AccountStore.getCurrentAccount()) {
        accountActions.getAccount(AuthenticationStore.getCookieAccountId()).then(() => {
            setGlobalHierarchyItem()
        })
    } else {
        setGlobalHierarchyItem()
    }
}

function getAppIdForIntercom() {
    return getCurrentEnviornment() === "PROD" ? "n9mo4v5d" : "hoo3xsbf"
}

function getCorrectUserEmailForIntercom(email: string, isFinddTestAccount: boolean) {
    if (getCurrentEnviornment() === "PROD") return email
    else if (getCurrentEnviornment() === "TEST" || getCurrentEnviornment() === "STAGING" || getCurrentEnviornment() === "DEV") {
        if (isFinddTestAccount) return email
        else return `${email}-${getCurrentEnviornment().charAt(0) + getCurrentEnviornment().slice(1).toLowerCase()}`
    }
}

function getCorrectCompanyNameForIntercom(name: string) {
    if (getCurrentEnviornment() === "PROD") return name
    else if (getCurrentEnviornment() === "TEST" || getCurrentEnviornment() === "STAGING" || getCurrentEnviornment() === "DEV") {
        return `${name}-${getCurrentEnviornment().charAt(0) + getCurrentEnviornment().slice(1).toLowerCase()}`
    }
}

function getCountFromStats(accountStatsDoc: IAccountStatsDocument, propertyName: string) {
    if (accountStatsDoc && accountStatsDoc[propertyName] !== null) return accountStatsDoc[propertyName]
    return 0
}

function getInfoFromClient(clientDoc: IClientDocument, propertyName: string) {
    if (propertyName === "CompanySize") return clientDoc !== null ? strings[ESizeOfBusiness[clientDoc.CompanySize]] : null
    return clientDoc !== null ? strings[ETypeOfBusiness[clientDoc.CompanyType]] : null
}

function getReleaseNotificationPreferences(user: IUserDocument, name: string) {
    return user.NotificationSettings && user.NotificationSettings.IntercomNotificationSettings && user.NotificationSettings.IntercomNotificationSettings[name]
}

function setGlobalHierarchyItem() {
    let globalFilterLevelId
    if (AuthenticationStore.getLoggedInUser().AccountUser) {
        const allowedHierarchyIds = userHelper.getSupervisorClickableHierarchyIds()
        globalFilterLevelId = HierarchySettingsStore.getGlobalHierarchyLevelIdCookie()
        if (!allowedHierarchyIds.includes(globalFilterLevelId)) {
            globalFilterLevelId = AuthenticationStore.getLoggedInUser().OrganizationHierarchyId
        }
    }
    else {
        globalFilterLevelId = null
    }
    hierarchySettingsActions.receiveHierarchyGlobalItem(hierarchyHelper.getHierarchyItemById(globalFilterLevelId))
}

export function logout() {
    if (enableIntercom()) {
        try {
            window["Intercom"]("shutdown")
        } catch (error) {
            console.error(error)
        }
    }
    api.logout()
    localStorage.setItem("findd_userId", "")
    localStorage.setItem("findd_accountId", "")
    localStorage.setItem("findd_ASPXAUTH", "")
    localStorage.setItem("findd_type", "")
    localStorage.setItem("findd_ExceptionFilterTypeStringForPayrollPage", "")
    localStorage.setItem("findd_ExceptionFilterTypeStringForTimecardPage", "")
    localStorage.setItem("findd_ShowDirectReportOnlyForEmployeesPage", "")
    localStorage.setItem("findd_ShowDirectReportOnlyForLeaveTrackingPage", "")
    localStorage.setItem("findd_ShowDirectReportOnlyForTimecardsPage", "")
    localStorage.setItem("findd_FilterTimecardByPunchHierarchy", "")
    localStorage.removeItem("selectedAccountManagerIdToNavigate")
    AuthenticationStore.deleteCookieAuth()
    dispatcher.dispatch({ type: "LOGOUT" })
    if (AuthenticationStore.getCookieSSOLogoutUrl()) {
        let logoutUrl = AuthenticationStore.getCookieSSOLogoutUrl()
        AuthenticationStore.deleteCookieSSOLogoutUrl()
        window.location.assign(logoutUrl)
    }
}

export function updateLoggedInUser(userId, user, successCb = null) {
    return api.updateUser(userId, user).then(res => {
        dispatcher.dispatch({ type: "UPDAE_LOGGED_IN_USER_DATA", data: res.data })
        successCb && typeof successCb === "function" &&
            successCb()
    })
}